/**
 * =====================================================
 * ErrorResponseHelper
 * =====================================================
 *
 * Centralized utility for normalizing error responses
 * into a user-displayable format.
 *
 * Responsibilities:
 * - Detect backend error categories (domain, validation)
 * - Extract meaningful, user-friendly error messages
 * - Provide a consistent ActionResponse-compatible output
 *
 * This helper acts as the final translation layer between
 * technical errors (API / network / exceptions) and
 * UI-consumable error states.
 */
export class ErrorResponseHelper {

	/**
	 * =====================================================
	 * getFirstValidationMessage
	 * =====================================================
	 *
	 * Extracts the first validation error code from a
	 * backend message string.
	 *
	 * Laravel often returns messages such as:
	 * - "contact.validation.lastname.mandatory (and 3 more errors)"
	 * - "contact.validation.lastname.mandatory (et 3 erreurs supplémentaires)"
	 *
	 * Since frontend translation keys never contain spaces,
	 * the first token is always a safe, usable error code.
	 */
	private static getFirstValidationMessage(message?: string): string {
		const emptyStr = "";
		const singleSpace = " ";
		return message?.split(singleSpace)[0] || emptyStr;
	}


	/**
	 * =====================================================
	 * getError
	 * =====================================================
	 *
	 * Normalizes any error into a standard ActionResponse.
	 *
	 * Resolution strategy:
	 * - Domain error → use backend-provided business message
	 * - Validation error → extract first validation code
	 * - Other errors → attempt message extraction or fallback
	 *
	 * This method guarantees a predictable error structure
	 * consumable by ResponseHook and UI layers.
	 */
	static getError(error: any, fallback = "Une erreur est survenue") {
		
		let message = fallback;
		const data = error.response?.data;
		if (data) {
			if (data.type === "validation") {
				message = data.errors.length > 0 
				? ErrorResponseHelper.getFirstValidationMessage(data.errors) 
				: (data.message?.i18n ?? fallback);
			}
			else {
				message = data.message?.i18n ?? fallback;  // <- correction ici
			}
		}

		return {
			status: "error",
			error: message,
		};
	}

}